import { useId, useState } from 'react';

import { cx } from '../../../shared/lib/cx.js';
import { toAppError } from '../../../shared/lib/errors.js';
import forms from '../../../shared/styles/forms.module.css';
import { EmojiPicker } from '../../../shared/ui/EmojiPicker.js';
import {
  DEFAULT_CATEGORY_EMOJI,
  resolveCategoryEmoji,
} from '../../../shared/ui/icons/categoryEmoji.js';
import { createCategory } from '../../categories/data/categoriesRepository.js';
import { useCategories } from '../../categories/hooks/useCategories.js';
import {
  duplicateCategoryMessage,
  findDuplicateCategory,
} from '../../categories/model/duplicateName.js';
import type { QuickAddCategory } from '../data/quickAddRepository.js';
import styles from './QuickAddControls.module.css';

/** Slots in the grid before "Thêm" takes the last one — 2 rows of 4 on a phone. */
const VISIBLE_SLOTS = 7;

/**
 * The category grid: the seven categories most likely to be tapped, then "Thêm" for the rest
 * — and for making one that does not exist yet.
 *
 * Favourites come first (they are ordered that way upstream), so the two rows on screen are
 * the household's own shortlist rather than an alphabet.
 */
export function CategoryPicker({
  categories,
  householdId,
  selectedId,
  onSelect,
  onCreated,
}: {
  categories: readonly QuickAddCategory[];
  householdId: string;
  selectedId: string;
  onSelect: (categoryId: string) => void;
  /** Called after a new category is stored, so the options can be reread. */
  onCreated: (categoryId: string) => Promise<void> | void;
}) {
  const [isExpanded, setExpanded] = useState(false);
  const [isCreating, setCreating] = useState(false);

  const selected = categories.find((category) => category.id === selectedId);
  const head = categories.slice(0, VISIBLE_SLOTS);
  // A category chosen from the full list keeps its place in the short grid afterwards.
  const shortlist =
    selected && !head.some((category) => category.id === selected.id)
      ? [...head.slice(0, VISIBLE_SLOTS - 1), selected]
      : head;
  const shown = isExpanded ? categories : shortlist;

  return (
    <section className={styles.section}>
      <p className={styles.sectionLabel}>Danh mục</p>

      <div className={styles.categoryGrid} role="group" aria-label="Danh mục">
        {shown.map((category) => (
          <button
            key={category.id}
            type="button"
            className={styles.categoryCell}
            aria-pressed={selectedId === category.id}
            onClick={() => onSelect(category.id)}
          >
            {/* aria-hidden: the emoji is decoration, so the button is named "Ăn uống" and not
                "money with wings Ăn uống" — for a screen reader and for a test alike. */}
            <span
              className={cx(
                styles.categoryBubble,
                selectedId === category.id && styles.categoryBubbleActive,
              )}
              aria-hidden="true"
            >
              {resolveCategoryEmoji(category)}
            </span>
            <span className={styles.categoryName}>{category.name}</span>
          </button>
        ))}

        {categories.length > VISIBLE_SLOTS || !isExpanded ? (
          <button
            type="button"
            className={styles.categoryCell}
            aria-expanded={isExpanded}
            onClick={() => setExpanded((open) => !open)}
          >
            <span className={styles.categoryBubble} aria-hidden="true">
              ⋯
            </span>
            <span className={styles.categoryName}>{isExpanded ? 'Thu gọn' : 'Thêm'}</span>
          </button>
        ) : null}
      </div>

      {isExpanded && !isCreating ? (
        <button type="button" className={styles.createLink} onClick={() => setCreating(true)}>
          + Tạo danh mục mới
        </button>
      ) : null}

      {isCreating ? (
        <NewCategoryForm
          householdId={householdId}
          onCancel={() => setCreating(false)}
          onCreated={async (categoryId) => {
            setCreating(false);
            setExpanded(false);
            await onCreated(categoryId);
            onSelect(categoryId);
          }}
        />
      ) : null}
    </section>
  );
}

/**
 * Creating a category from inside Quick Add: name, group and icon.
 *
 * The group is asked for because every category has one now (N10) and the report groups by
 * it; it arrives pre-selected, so the fast path is still name → Tạo. Ordering and favourites
 * stay on the category screen — asking for those here would turn "the shop had no matching
 * category" into a form to fill in.
 *
 * Deliberately NOT a `<form>`: this panel lives inside the expense form, and a nested form is
 * invalid HTML — the browser submitted the outer one and reloaded the page, losing the amount
 * the user had already typed (observed, then fixed). Enter in the name field still creates.
 */
function NewCategoryForm({
  householdId,
  onCreated,
  onCancel,
}: {
  householdId: string;
  onCreated: (categoryId: string) => Promise<void> | void;
  onCancel: () => void;
}) {
  const nameId = useId();
  const parentFieldId = useId();
  const [name, setName] = useState('');
  const [parentId, setParentId] = useState('');
  const [emoji, setEmoji] = useState<string>(DEFAULT_CATEGORY_EMOJI);
  const [message, setMessage] = useState<string | null>(null);
  const [isSaving, setSaving] = useState(false);

  // The full list, including hidden categories and the income ones — the Quick Add options
  // only carry active expense categories, which is not enough to answer "is this name taken".
  // Loaded only while this panel is open, so opening Quick Add stays one read.
  const categoriesState = useCategories();
  const parents =
    categoriesState.status === 'ready'
      ? categoriesState.categories.filter(
          (category) => category.categoryType === 'expense' && category.parentId === null,
        )
      : [];
  const selectedParentId = parentId === '' ? (parents[0]?.id ?? '') : parentId;
  const duplicate =
    categoriesState.status === 'ready'
      ? findDuplicateCategory(categoriesState.categories, {
          name,
          categoryType: 'expense',
          householdId,
        })
      : null;

  async function handleCreate() {
    if (name.trim() === '') {
      setMessage('Vui lòng nhập tên danh mục.');
      return;
    }
    if (duplicate !== null) return;
    if (selectedParentId === '') {
      setMessage('Chưa tải được danh sách nhóm. Thử lại sau giây lát.');
      return;
    }

    setSaving(true);
    setMessage(null);
    try {
      const categoryId = await createCategory(householdId, {
        name,
        categoryType: 'expense',
        icon: emoji,
        parentId: selectedParentId,
      });
      await onCreated(categoryId);
    } catch (error) {
      setMessage(toAppError(error).message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className={styles.createForm}>
      <div className={forms.field}>
        <label htmlFor={nameId}>Tên danh mục mới</label>
        <input
          id={nameId}
          className={forms.input}
          type="text"
          value={name}
          placeholder="Ví dụ: Cắt tóc"
          autoComplete="off"
          enterKeyHint="done"
          aria-invalid={duplicate !== null}
          onChange={(event) => setName(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              // Would otherwise submit the surrounding expense form.
              event.preventDefault();
              void handleCreate();
            }
          }}
        />
        {duplicate ? (
          <p className={forms.fieldError} role="alert">
            {duplicateCategoryMessage(duplicate)}
          </p>
        ) : null}
      </div>

      <div className={forms.field}>
        <label htmlFor={parentFieldId}>Thuộc nhóm</label>
        <select
          id={parentFieldId}
          className={forms.input}
          value={selectedParentId}
          onChange={(event) => setParentId(event.target.value)}
        >
          {parents.map((parent) => (
            <option key={parent.id} value={parent.id}>
              {parent.name}
            </option>
          ))}
        </select>
      </div>

      <p className={styles.sectionLabel}>Chọn biểu tượng</p>
      <EmojiPicker value={emoji} onChange={setEmoji} />

      {message ? (
        <p className={forms.error} role="alert">
          {message}
        </p>
      ) : null}

      <div className={forms.actions}>
        <button type="button" className={forms.secondary} onClick={onCancel}>
          Hủy
        </button>
        <button
          type="button"
          className={forms.primary}
          disabled={isSaving || name.trim() === '' || duplicate !== null}
          onClick={() => void handleCreate()}
        >
          {isSaving ? 'Đang tạo…' : 'Tạo và chọn'}
        </button>
      </div>
    </div>
  );
}
